/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 * Simple React 19 compatible file browser component
 */

import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ListGroup, Button, Form, FormControl } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFile, faChevronRight, faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';

const SimpleFileBrowser = ({ 
    files = [], 
    onFileSelect, 
    onFolderSelect,
    selectedFiles = [],
    singleSelection = true,
    style = {},
    className = "",
    showSearch = true
}) => {
    const [expandedFolders, setExpandedFolders] = useState(new Set());
    const [selectedItems, setSelectedItems] = useState(selectedFiles);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter files based on search term (contains functionality)
    const filteredFiles = useMemo(() => {
        if (!searchTerm.trim()) {
            return files;
        }
        const searchLower = searchTerm.toLowerCase();
        return files.filter(file => 
            file.name.toLowerCase().includes(searchLower) || 
            file.path.toLowerCase().includes(searchLower)
        );
    }, [files, searchTerm]);

    const toggleFolder = (folderPath) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(folderPath)) {
            newExpanded.delete(folderPath);
        } else {
            newExpanded.add(folderPath);
        }
        setExpandedFolders(newExpanded);
    };

    const handleItemClick = (item, event) => {
        event.preventDefault();
        event.stopPropagation();
        
        console.log('SimpleFileBrowser handleItemClick called with:', item);
        
        let newSelected;
        if (singleSelection) {
            // Single selection only - replace current selection with new one
            newSelected = [item.path];
        } else {
            // Multiple selection - toggle selection
            newSelected = selectedItems.includes(item.path) 
                ? selectedItems.filter(path => path !== item.path)
                : [...selectedItems, item.path];
        }
        
        setSelectedItems(newSelected);
        
        // For directories, also call onFolderSelect if provided, but always call onFileSelect
        if (item.isDirectory) {
            if (onFolderSelect) {
                onFolderSelect(item);
            }
        }
        
        // Always call onFileSelect for both files and directories
        console.log('SimpleFileBrowser calling onFileSelect with:', newSelected);
        if (onFileSelect) {
            onFileSelect(newSelected);
        }
    };

    const renderFileItem = (item, level = 0) => {
        const isExpanded = expandedFolders.has(item.path);
        const isSelected = selectedItems.includes(item.path);
        const indentStyle = { paddingLeft: `${level * 20}px` };

        return (
            <div key={item.path}>
                <ListGroup.Item
                    onClick={(event) => handleItemClick(item, event)}
                    style={{
                        ...indentStyle,
                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        padding: '0.25rem 0.5rem'
                    }}
                    className={isSelected ? 'active' : ''}
                >
                    {item.isDirectory ? (
                        <>
                            {/* Hide chevron for folders - no expansion needed */}
                            <span style={{ width: '16px' }}></span>
                            <FontAwesomeIcon icon={faFolder} />
                        </>
                    ) : (
                        <>
                            <span style={{ width: '16px' }}></span>
                            <FontAwesomeIcon icon={faFile} />
                        </>
                    )}
                    <span>{item.name}</span>
                </ListGroup.Item>
                
                {item.isDirectory && isExpanded && item.children && (
                    <div>
                        {item.children.map(child => renderFileItem(child, level + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={{ ...style, display: 'flex', flexDirection: 'column', height: '100%' }} className={className}>
            {showSearch && (
                <div style={{ padding: '10px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
                    <div style={{ position: 'relative' }}>
                        <FontAwesomeIcon 
                            icon={faSearch} 
                            style={{ 
                                position: 'absolute', 
                                left: '10px', 
                                top: '50%', 
                                transform: 'translateY(-50%)',
                                color: '#6c757d',
                                fontSize: '14px'
                            }} 
                        />
                        <FormControl
                            type="text"
                            placeholder="Search files..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                paddingLeft: '35px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>
            )}
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <ListGroup variant="flush">
                    {filteredFiles.length > 0 ? (
                        filteredFiles.map(item => renderFileItem(item))
                    ) : (
                        <ListGroup.Item style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
                            {searchTerm ? `No files found matching "${searchTerm}"` : 'No files available'}
                        </ListGroup.Item>
                    )}
                </ListGroup>
            </div>
        </div>
    );
};

SimpleFileBrowser.propTypes = {
    files: PropTypes.array,
    onFileSelect: PropTypes.func,
    onFolderSelect: PropTypes.func,
    selectedFiles: PropTypes.array,
    singleSelection: PropTypes.bool,
    style: PropTypes.object,
    className: PropTypes.string,
    showSearch: PropTypes.bool
};

export default SimpleFileBrowser;
